import React, { Component } from "react";
import { getProperty } from "../../../CommonFunctions";
import Icon from "./Icon";
import Modal from 'react-responsive-modal'

class FileUploader extends Component {
  constructor(props) {
    super(props);

    this.fileInputRef = React.createRef();

    this.state = {
      selectedFile: null,
      uploading: false,
      error: "",
      dragOver: false,
      files: props && props.files ? props.files : [],
      previewFile: null,
    };
  }

  componentDidUpdate(prevProps) {
    const prevFiles = prevProps && prevProps.files;
    const currentFiles = this.props && this.props.files;

    if (prevFiles !== currentFiles) {
      this.setState({
        files: currentFiles ? currentFiles : [],
      });
    }
  }

  /* ---------------- CONFIG ---------------- */

  getMaxSizeKB = () => {
    const maxSizeKB = getProperty(this.props, "maxSizeKB");
    if (maxSizeKB !== "" && maxSizeKB != null) {
      return parseInt(maxSizeKB, 10);
    }
    return 100;
  };

  getMaxFiles = () => {
    const maxFiles = getProperty(this.props, "maxFiles");
    if (maxFiles !== "" && maxFiles != null) {
      return parseInt(maxFiles, 10);
    }
    return 1;
  };

  getAllowedExtensions = () => {
    const map = {
      image: ["jpg", "jpeg", "png", "gif", "webp"],
      pdf: ["pdf"],
      excel: ["xls", "xlsx"],
      doc: ["doc", "docx"],
    };

    const supportFile = getProperty(this.props, "supportFile");

    if (!supportFile || !Array.isArray(supportFile)) return null;

    let allowed = [];

    for (let i = 0; i < supportFile.length; i++) {
      const type = supportFile[i];
      if (Object.prototype.hasOwnProperty.call(map, type)) {
        allowed = allowed.concat(map[type]);
      }
    }

    return allowed;
  };

  validateFile = (file) => {
    const maxSizeBytes = this.getMaxSizeKB() * 1024;

    if (file.size > maxSizeBytes) {
      this.setState({
        error: "File size exceeds " + this.getMaxSizeKB() + " KB",
      });
      return false;
    }

    const allowed = this.getAllowedExtensions();

    if (allowed && allowed.length) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (allowed.indexOf(ext) === -1) {
        this.setState({ error: "File type not supported" });
        return false;
      }
    }

    return true;
  };

  /* ---------------- MAIN UPLOAD FUNCTION ---------------- */
  
  processAndUploadFile = (file) => {
    // Clear previous errors
    this.setState({ error: "" });
    
    // Check if file exists
    if (!file) {
      this.setState({ error: "Please select a file" });
      return;
    }

    // Check max files limit
    if (this.state.files.length >= this.getMaxFiles()) {
      this.setState({
        error: "Maximum " + this.getMaxFiles() + " files allowed",
      });
      this.clearFileInput();
      return;
    }

    // Validate file
    if (!this.validateFile(file)) {
      this.clearFileInput();
      return;
    }

    // Start upload
    this.uploadFile(file);
  };

  /* ---------------- INPUT ---------------- */

  triggerFileInput = () => {
    if (this.fileInputRef.current && !this.state.uploading) {
      this.fileInputRef.current.click();
    }
  };

  onFileChange = (event) => {
    if (event && event.target && event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      // Set selected file and immediately process it
      this.setState({ selectedFile: file }, () => {
        this.processAndUploadFile(file);
      });
    }
  };

  /* ---------------- DRAG & DROP ---------------- */

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({ dragOver: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    this.setState({ dragOver: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e && e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      this.setState({
        selectedFile: file,
        dragOver: false,
        error: "",
      }, () => {
        this.processAndUploadFile(file);
      });
      
      e.dataTransfer.clearData();
    }
  };

  /* ---------------- UPLOAD ---------------- */

  uploadFile = (fileToUpload) => {
    // Prevent multiple uploads
    if (this.state.uploading) {
      return;
    }

    this.setState({ uploading: true, error: "" });

    const storageFolder = getProperty(this.props, "storageFolder") || "uploads";
    const fileName = getProperty(this.props, "fileName") || "";
    const uploadFn = getProperty(this.props, "uploadFileToServer");

    if (!uploadFn) {
      this.setState({
        uploading: false,
        error: "Upload function not provided",
      });
      this.clearFileInput();
      return;
    }

    uploadFn(fileToUpload, storageFolder, fileName)
      .then((uploadedUrl) => {
        console.log("uploadUrl",uploadedUrl);
        const newFile = {
          name: fileToUpload.name,
          url: uploadedUrl,
          type: fileToUpload.type,
        };

        const updatedFiles = [...this.state.files, newFile];

        this.setState({
          files: updatedFiles,
          selectedFile: null,
          uploading: false,
        });

        // Clear the file input
        this.clearFileInput();

        const onSuccess = getProperty(this.props, "onUploadSuccess");
        if (onSuccess) onSuccess(updatedFiles);
      })
      .catch((err) => {
        const message = err && err.message ? err.message : "Upload failed";

        this.setState({
          uploading: false,
          error: message,
          selectedFile: null,
        });
        
        // Clear the file input on error
        this.clearFileInput();
      });
  };

  clearFileInput = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  /* ---------------- DELETE FILE ---------------- */

  deleteFile = (indexToDelete, e) => {
    e.stopPropagation();
    
    const updatedFiles = this.state.files.filter((_, index) => index !== indexToDelete);
    
    this.setState({ files: updatedFiles });
    
    const onFileDelete = getProperty(this.props, "onFileDelete");
    if (onFileDelete) {
      onFileDelete(updatedFiles);
    }
  };

  /* ---------------- PREVIEW ---------------- */

  openPreview = (file) => {
    this.setState({ previewFile: file });
  };

  closePreview = () => {
    this.setState({ previewFile: null });
  };

  /* ---------------- STYLES ---------------- */

  getDropzoneStyle = () => {
    const { dragOver, uploading } = this.state;

    return {
      border: dragOver ? "2px dashed #3b82f6" : "2px dashed #ccc",
      backgroundColor: dragOver ? "#eff6ff" : "#fff",
      borderRadius: 10,
      padding: 20,
      textAlign: "center",
      cursor: uploading ? "not-allowed" : "pointer",
      transition: "0.2s",
      opacity: uploading ? 0.6 : 1,
    };
  };

  renderPreview = () => {
    const file = this.state.previewFile;
    console.log("file",file);
    if (!file) return null;

    const isImage = file.type && file.type.indexOf("image") === 0;

   return (
  <Modal
    open={file} 
    center
    onClose={this.closePreview}
    styles={{
      modal: {
        maxWidth: '90%',
        width: '90%',
        maxHeight: '90vh',
        padding: '0',
        borderRadius: '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '@media (max-width: 768px)': {
          maxWidth: '95%',
          width: '95%',
          maxHeight: '85vh',
          borderRadius: '8px'
        }
      }
    }}
  >
    <div style={{ 
      overflow: 'auto',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      maxHeight: '90vh',
      width: '100%',
      '@media (max-width: 768px)': {
        maxHeight: '85vh',
        padding: '8px'
      }
    }}>
      {isImage ? (
        <img 
          src={file.url} 
          alt="Preview" 
          style={{ 
            maxWidth: '100%',
            maxHeight: '90vh',
            objectFit: 'contain',
            width: 'auto',
            height: 'auto',
            '@media (max-width: 768px)': {
              maxHeight: '80vh',
              maxWidth: '95%'
            }
          }} 
        />
      ) : (
        <iframe
          src={file.url}
          title="preview"
          style={{ 
            width: '100%',
            height: '80vh',
            border: 'none',
            maxWidth: '1200px',
            '@media (max-width: 768px)': {
              height: '70vh',
              width: '100%'
            },
            '@media (max-width: 480px)': {
              height: '60vh'
            }
          }}
        />
      )}
    </div>
  </Modal>
);
  };

  render() {
    const { uploading, error, selectedFile, files } = this.state;

    const allowedExtensions = this.getAllowedExtensions();
    const acceptAttribute = allowedExtensions && allowedExtensions.length 
      ? allowedExtensions.map(ext => `.${ext}`).join(',')
      : undefined;

    return (
      <div style={{ fontFamily: "Arial" }}>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>

        <div
          style={this.getDropzoneStyle()}
          onClick={this.triggerFileInput}
          onDragOver={this.handleDragOver}
          onDragLeave={this.handleDragLeave}
          onDrop={this.handleDrop}
        >
          <input
            ref={this.fileInputRef}
            type="file"
            onChange={this.onFileChange}
            style={{ display: "none" }}
            accept={acceptAttribute}
          />

          {uploading ? (
            <div>
              <div
                style={{
                  width: 30,
                  height: 30,
                  border: "3px solid #3b82f6",
                  borderTop: "3px solid transparent",
                  borderRadius: "50%",
                  margin: "0 auto",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p style={{ marginTop: 10 }}>Uploading...</p>
            </div>
          ) : (
            <div>
              <div
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: "50%",
                  background: "#eff6ff",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  margin: "0 auto",
                  fontSize: 22,
                }}
              >
                 <Icon type="download"/>
              </div>

              <p style={{ marginTop: 10, fontWeight: "bold" }}>
                Click or drag files to upload
              </p>

              <p style={{ fontSize: 12, color: "#666" }}>
                {allowedExtensions ? allowedExtensions.join(', ') : 'All files'}
              </p>

              <p style={{ fontSize: 11, color: "#999", marginTop: 5 }}>
                Max {this.getMaxFiles()} files • {this.getMaxSizeKB()}KB each
              </p>
            </div>
          )}
        </div>

        {selectedFile && !uploading && (
          <div style={{ marginTop: 10, fontSize: 13, color: "#059669" }}>
            ✅ Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
          </div>
        )}

        {error && (
          <div style={{ color: "red", marginTop: 10, fontSize: 12 }}>
            ❌ {error}
          </div>
        )}

        {files.length > 0 && (
          <div style={{ marginTop: 15, borderTop: "1px solid #eee" }}>
            <div style={{ marginBottom: 10 ,fontWeight:"500"}}>Uploaded Files:</div>
            {files.map((f, i) => (
              <div
                key={i}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #eee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <div 
                  onClick={() => this.openPreview(f)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flex: 1,
                  }}
                >
                  <span>📎</span>
                  <span>{f.name}</span>
                </div>
                <button
                  onClick={(e) => this.deleteFile(i, e)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 18,
                    padding: "4px 8px",
                    borderRadius: 4,
                    color: "#ef4444",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fee2e2";
                    e.currentTarget.style.transform = "scale(1.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                <Icon type="delete"/>
                </button>
              </div>
            ))}
          </div>
        )}

        {this.renderPreview()}
      </div>
    );
  }
}

export default FileUploader;